import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { SectionIntro } from "@/components/site/SectionHead";
import { Reveal } from "@/components/site/motion";
import { PostCard, PostSkeleton } from "@/components/logbook/PostCard";
import { SubscribePanel } from "@/components/logbook/SubscribePanel";
import { useLogbookPosts } from "@/hooks/useLogbook";
import { LINKS } from "@/content/site";

const Carousel = () => {
  const { posts, loading, error } = useLogbookPosts();
  const track = useRef(null);
  const scroll = (dir) => track.current?.scrollBy({ left: dir * (track.current.clientWidth * 0.8), behavior: "smooth" });
  const items = posts.slice(0, 10);

  useEffect(() => {
    if (track.current) track.current.scrollLeft = 0;
  }, [posts.length]);

  if (error && !items.length) {
    return (
      <div className="flex h-full min-h-[300px] flex-col justify-center rounded-3xl border border-white/[0.08] p-8" data-testid="logbook-carousel-fallback">
        <p className="text-2xl font-[540] text-fg">Recent entries are taking a moment to load.</p>
        <a href={LINKS.beehiiv} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-signal">Read the Logbook on Beehiiv <ArrowUpRight className="h-4 w-4" /></a>
      </div>
    );
  }
  return (
    <div data-testid="logbook-carousel">
      <div className="mb-6 flex items-center justify-between">
        <p className="eyebrow text-fg-3">Latest entries</p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => scroll(-1)} className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-fg transition-colors hover:border-signal hover:text-signal" aria-label="Previous entries" data-testid="logbook-carousel-prev"><ArrowLeft className="h-4 w-4" /></button>
          <button type="button" onClick={() => scroll(1)} className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-fg transition-colors hover:border-signal hover:text-signal" aria-label="Next entries" data-testid="logbook-carousel-next"><ArrowRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div ref={track} className="no-scrollbar -mr-5 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 pr-5 sm:-mr-8 sm:pr-8 lg:-mr-12 lg:pr-12">
        {loading && !items.length
          ? [0, 1, 2].map((k) => <div key={k} className="w-[78vw] shrink-0 snap-start sm:w-[320px]"><PostSkeleton /></div>)
          : items.map((p) => <PostCard key={p.slug} post={p} className="w-[78vw] shrink-0 snap-start sm:w-[320px]" />)}
        <Link to="/logbook" className="group flex w-[60vw] shrink-0 snap-start flex-col justify-end rounded-2xl border border-dashed border-white/15 p-6 transition-colors hover:border-signal sm:w-[240px]" data-testid="logbook-carousel-all">
          <span className="text-2xl font-[540] tracking-[-0.02em] text-fg">The full Logbook</span>
          <span className="mt-2 inline-flex items-center gap-1 text-sm text-signal">All entries <ArrowUpRight className="h-3.5 w-3.5" /></span>
        </Link>
      </div>
    </div>
  );
};

export const LogbookSection = () => (
  <section className="py-16 sm:py-24" data-testid="logbook-section" data-analytics-section="logbook">
    <div className="shell">
      <SectionIntro
        label="Logbook"
        title="Follow the build."
        text="Notes from Hash on ships, technology and what we’re learning."
      />
      <div className="mt-16 grid gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-4" id="subscribe"><SubscribePanel /></Reveal>
        <Reveal className="min-w-0 lg:col-span-8" delay={0.08}><Carousel /></Reveal>
      </div>
    </div>
  </section>
);
